import React, { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuthContext } from "@/context/AuthContext";
import { useSearchCode } from "@/api";

interface UrlCodeContextType {
  isProcessing: boolean;
  error: string | null;
}

const UrlCodeContext = createContext<UrlCodeContextType | undefined>(undefined);

export function UrlCodeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeParam = searchParams.get("code");
  const [redirecting, setRedirecting] = useState(true);
  const [searchCode] = useSearchCode();

  const navigate = useNavigate();

  const processUrlCode = async (code: string) => {
    setIsProcessing(true);
    setError(null);

    try {
      const result = await searchCode({ code });

      if (!result.codeData.raveId && result.editView) {
        navigate(`/raves/start?setCode=${code}`);
        return;
      }

      if (result.codeData.raveId && result.editView) {
        navigate(`/raves/${result.codeData.raveId}/edit/edit`);
        return;
      }

      if (result.codeData.raveId && !result.editView) {
        navigate(`/raves/for-you/${result.codeData.raveId}`);
        return;
      }

      throw new Error("Code not available for redemption");
    } catch (err) {
      console.error("Error processing URL code:", err);
      setError("Failed to process code");
    } finally {
      setIsProcessing(false);
      setRedirecting(false);
    }
  };

  useEffect(() => {
    if (codeParam) {
      processUrlCode(codeParam);
    } else {
      setRedirecting(false);
    }
  }, [codeParam, navigate, user.company]);

  if (redirecting) {
    return <div>loading...</div>;
  }

  const value: UrlCodeContextType = {
    isProcessing,
    error,
  };

  return (
    <UrlCodeContext.Provider value={value}>{children}</UrlCodeContext.Provider>
  );
}

export function useUrlCodeContext() {
  const context = useContext(UrlCodeContext);
  if (context === undefined) {
    throw new Error("useUrlCodeContext must be used within a UrlCodeProvider");
  }
  return context;
}
